import { useId, type ReactNode } from "react";

import { cn } from "@/lib/cn";
import {
  CONTENT_ENCODING_LABELS,
  MESSAGE_ROLE_LABELS,
  VISIBILITY_LABELS,
  describeExactness,
  describeInvocation,
  describeOmission,
  sameDigest,
  segmentBidiControls,
  type ContentBody,
  type PromptCall,
} from "@/lib/previewPromptCall";
import { Badge } from "@/ui/Badge";
import { Notice } from "@/ui/Notice";

import { Identifier } from "@/routes/previewRunParts";

/**
 * ONE prompt call, rendered exactly (#1099, approved design §10.3).
 *
 * The effective system prompt, the messages sent in order, the structured
 * result and the patient-facing answer where the producer recorded them — each
 * body in exactly one of the contract's three states, and each inline body as
 * the bytes the response carried, untouched.
 *
 * ## Content is text, and only text (ADR 0013 N4; design §7.4)
 *
 * Every body reaches the screen as a text node inside its own block. Nothing in
 * it is interpreted as markup, a link or a control: an HTML tag is characters,
 * a URL is characters, a script is characters. Each block is direction-isolated
 * (`dir="auto"`, `unicode-bidi: plaintext`) so a right-to-left prompt reads
 * right-to-left inside a left-to-right page and vice versa, and so nothing
 * inside a body can reorder the chrome around it. The Unicode bidirectional
 * control characters — the ones that reorder text silently — are each rendered
 * inside their own isolated, visibly marked element and kept in the text, so
 * the body stays byte-exact while no control in it can reach its neighbours.
 *
 * MOVED HERE UNCHANGED by #1107, from `routes/PreviewRunPromptCall.tsx` where
 * #1099 wrote it. The Compare tab renders each side of a changed prompt pair
 * with it, and a second copy of the inert-text discipline above is exactly the
 * thing that must not exist twice.
 */
export function ExactPromptCall({ call }: { call: PromptCall }) {
  const { invocation } = call;
  const resultAbsence =
    invocation.outcome === "not_invoked"
      ? "the model was not called, so there is no result."
      : invocation.outcome === "failed"
        ? "the model call failed, so there is no result."
        : null;

  return (
    <div className="space-y-5">
      {call.visibility === "restricted_technical" ? (
        <Notice tone="warning">
          Restricted technical content. Every body of this call was withheld
          at source by policy; its identity, sizes, digests and outcome are
          shown. Nothing is hidden by this page, and nothing withheld is
          reconstructed.
        </Notice>
      ) : null}
      {invocation.outcome === "not_invoked" ? (
        <Statement>{describeInvocation(invocation)}</Statement>
      ) : null}
      {invocation.outcome === "failed" ? (
        <Notice tone="destructive">{describeInvocation(invocation)}</Notice>
      ) : null}

      <BodySection
        title="System prompt"
        body={call.systemPrompt}
        absent="No system prompt slot was carried by this event."
      />

      <MessageList call={call} />

      <BodySection
        title="Structured result"
        body={call.structuredResult}
        absent={
          resultAbsence !== null
            ? `No structured result: ${resultAbsence}`
            : "No structured result was recorded for this call."
        }
      />

      <BodySection
        title="Patient-facing answer"
        body={call.patientFacingOutput}
        absent={
          resultAbsence !== null
            ? `No patient-facing answer: ${resultAbsence}`
            : "No patient-facing answer was recorded. Only the conversation call type answers the patient; every other call type answers the runtime."
        }
      />
    </div>
  );
}

function MessageList({ call }: { call: PromptCall }) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <h4 id={headingId} className="text-sm font-medium text-foreground">
        Messages sent, in order
      </h4>
      {call.messages.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {call.invocation.outcome === "not_invoked"
            ? "No messages were sent: the model was not called."
            : "No messages were carried by this event."}
        </p>
      ) : (
        <ol className="space-y-3">
          {call.messages.map((message, index) => (
            <li key={index} className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="outline">
                  <span dir="ltr">{index + 1}</span>
                </Badge>
                <Badge tone="neutral">{MESSAGE_ROLE_LABELS[message.role]}</Badge>
              </div>
              <PromptBody
                body={message.content}
                sameAsSystemPrompt={
                  message.content.state === "reference" &&
                  sameDigest(message.content, call.systemPrompt)
                }
              />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function BodySection({
  title,
  body,
  absent,
}: {
  title: string;
  body: ContentBody | null;
  absent: string;
}) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <h4 id={headingId} className="text-sm font-medium text-foreground">
        {title}
      </h4>
      {body === null ? (
        <p className="text-sm text-muted-foreground">{absent}</p>
      ) : (
        <PromptBody body={body} />
      )}
    </section>
  );
}

/** A recorded fact stated in the clinician's reading order, in a plain bordered line. */
function Statement({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">
      {children}
    </p>
  );
}

/**
 * One body in one of its three states.
 *
 * INLINE text is rendered as a text node inside a `<pre>` — every byte the
 * response carried, in order, unchanged. `whitespace-pre-wrap` keeps line
 * breaks and runs of spaces; `overflow-wrap: anywhere` lets a long token wrap
 * instead of scrolling the page sideways. `dir="auto"` plus
 * `unicode-bidi: plaintext` gives each paragraph of the body its own
 * direction from its own first strong character — a Hebrew line reads
 * right-to-left, an English one left-to-right, whichever way the document
 * runs — and isolates the block, so nothing inside it can reorder the chrome
 * around it. `text-start` keeps alignment on the logical start.
 *
 * EXPORTED for #1101's layer view, which renders one provenance layer's own
 * bytes with the same discipline. A second copy of the inert-text rules above
 * is exactly the thing that must not exist twice on this lane.
 */
export function PromptBody({
  body,
  sameAsSystemPrompt = false,
}: {
  body: ContentBody;
  /** Whether a referenced body is, by digest, the system prompt shown above it. */
  sameAsSystemPrompt?: boolean;
}) {
  switch (body.state) {
    case "inline":
      return (
        <div className="space-y-1">
          <pre
            data-slot="prompt-body"
            dir="auto"
            className={cn(
              "max-w-full whitespace-pre-wrap rounded-md border border-border bg-muted px-3 py-2 text-start font-mono text-xs leading-5 text-foreground",
              "[overflow-wrap:anywhere] [unicode-bidi:plaintext]",
            )}
          >
            <InertText text={body.text} />
          </pre>
          {body.exactness === "size_mismatch" ? (
            <Notice tone="destructive">{describeExactness(body)}</Notice>
          ) : null}
          <BodyFacts body={body}>{describeExactness(body)}</BodyFacts>
        </div>
      );
    case "reference":
      return (
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">
            Carried by reference: the text is not included in this event.
            {sameAsSystemPrompt ? " It is the system prompt above, by digest." : ""}
          </p>
          <BodyFacts body={body} />
        </div>
      );
    case "omitted":
      return (
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{describeOmission(body)}</p>
          <BodyFacts body={body} />
        </div>
      );
  }
}

/** Encoding, size, digest and visibility class — what the producer declared about the bytes. */
function BodyFacts({ body, children }: { body: ContentBody; children?: ReactNode }) {
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      {children !== undefined ? <span>{children}</span> : null}
      {body.encoding !== null ? <span>{CONTENT_ENCODING_LABELS[body.encoding]}</span> : null}
      {body.state !== "inline" && body.byteSize !== null ? (
        <span>
          <span dir="ltr">{body.byteSize}</span> bytes declared
        </span>
      ) : null}
      {body.contentHash !== null ? (
        <span className="inline-flex items-baseline gap-1">
          Digest <Identifier value={body.contentHash} />
        </span>
      ) : null}
      {body.visibility !== null ? <span>{VISIBILITY_LABELS[body.visibility]}</span> : null}
    </p>
  );
}

/**
 * The body's text, byte for byte, with every bidirectional control character
 * in its own isolated, visibly marked element.
 *
 * The control character stays IN the text — the element's text content is the
 * body exactly — but it is wrapped so that its effect is confined: an
 * isolated element with a control inside it and nothing else reorders
 * nothing outside it. The marker (`U+202E`, drawn by CSS from the data
 * attribute) makes the invisible character reviewable, and the accessible
 * name says what it is. Text between controls is a plain text node.
 */
function InertText({ text }: { text: string }) {
  return (
    <>
      {segmentBidiControls(text).map((segment, index) =>
        segment.kind === "text" ? (
          segment.text
        ) : (
          <span
            key={index}
            role="img"
            aria-label={`${segment.codePoint} ${segment.name}, a bidirectional control character in the prompt`}
            title={`${segment.codePoint} ${segment.name}: a bidirectional control character in the prompt, shown as a marker and isolated so it cannot reorder the surrounding text`}
            data-bidi-control={segment.codePoint}
            dir="ltr"
            className="mx-0.5 inline-block rounded border border-warning-foreground/40 bg-warning px-1 align-baseline font-sans text-[0.625rem] font-semibold text-warning-foreground [unicode-bidi:isolate] before:content-[attr(data-bidi-control)]"
          >
            {segment.character}
          </span>
        ),
      )}
    </>
  );
}
